import axios from 'axios';

import { encodeProductPathSegment } from '@apple/utils/url';

import { type Plant, plantSchema } from '@apple/features/plants/models/models';

import type {
	EndOfLifeReplacement,
	Inventory,
	ProductImageUploadResponse,
	VerifyIdsResponse,
	WarehousePrice,
} from '../models/management';
import {
	endOfLifeReplacementSchema,
	inventorySchema,
	verifyIdsResponseSchema,
	warehousePriceSchema,
} from '../models/management';
import type { ProductKey } from '../models/product';

/**
 * Gets current inventory information for the given item.
 * @see Manzanita.Web.Features.Catalog.ProductsController
 */
export async function getInventory(itemId: ProductKey): Promise<Inventory[]> {
	const response = await axios.get(
		`/api/management/products/${encodeProductPathSegment(itemId)}/inventory`,
	);

	return inventorySchema.array().parse(response.data);
}

/**
 * Gets current price information for the given item.
 * @see Manzanita.Web.Features.Catalog.ProductsController
 */
export async function getPrices(itemId: ProductKey): Promise<WarehousePrice[]> {
	const response = await axios.get(
		`/api/management/products/${encodeProductPathSegment(itemId)}/prices`,
	);

	return warehousePriceSchema.array().parse(response.data);
}

/**
 * Gets all end of life records where the replacement SKU belongs to the given item.
 * @see Manzanita.Web.Features.Catalog.ProductsController
 */
export async function getEolReplacementRecords(
	itemId: ProductKey,
): Promise<EndOfLifeReplacement[]> {
	const response = await axios.get(
		`/api/management/products/${encodeProductPathSegment(itemId)}/eol-replacements`,
	);

	return endOfLifeReplacementSchema.array().parse(response.data);
}

/**
 * Gets all plants available per inventory.
 * @see Manzanita.Web.Features.Catalog.ProductsController
 */
export async function getPlantsWithInventory(itemId: ProductKey): Promise<Plant[]> {
	const response = await axios.get(
		`/api/management/products/${encodeProductPathSegment(itemId)}/plants`,
	);

	return plantSchema.array().parse(response.data);
}

/**
 * Verifies if the given products exist.
 * @see Manzanita.Web.Features.Catalog.ProductsController
 */
export async function verifyIds(itemIds: string[]): Promise<VerifyIdsResponse> {
	const response = await axios.post('/api/management/products/verify-ids', itemIds);
	return verifyIdsResponseSchema.parse(response.data);
}

/**
 * Gets all SKUs in the system that are not assigned to a product in the catalog.
 * @see Manzanita.Web.Features.Catalog.ProductsController
 */
export async function getUnassignedSkus(): Promise<ProductKey[]> {
	const response = await axios.get<ProductKey[]>(
		'/api/management/products/inventory/unassigned-skus',
	);
	return response.data;
}

/**
 * Uploads product image
 * @see Manzanita.Web.Features.Catalog.ProductImagesController
 */
export async function uploadProductImage(
	file: File,
	itemId: ProductKey,
): Promise<ProductImageUploadResponse> {
	const body = new FormData();
	body.append('file', file);
	body.append('itemId', itemId);

	const response = await axios.post<ProductImageUploadResponse>(
		'/api/management/products/images',
		body,
	);

	return response.data;
}
